import { apiBaseURL } from "./config"
import * as sentry from "./sentry"
import * as SSR from "../api/hooks/lib/ssr.js"
import { get } from "svelte/store"
import { api2Key, apiKey } from "./store"

// [MIT License](LICENSE.md) © [Jason Miller](https://jasonformat.com/)
const _f = function (url, options): Promise<Response> {
    if (typeof XMLHttpRequest === "undefined") {
        return Promise.resolve(null)
    }

    options = options || {}
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const keys = []
        const all = []
        const headers = {}

        const response = (): Response => ({
            ok: ((request.status / 100) | 0) == 2, // 200-299
            statusText: request.statusText,
            status: request.status,
            url: request.responseURL,
            text: () => Promise.resolve(request.responseText),
            json: () => Promise.resolve(request.responseText).then(JSON.parse),
            blob: () => Promise.resolve(new Blob([request.response])),
            clone: response,
            headers: {
                // @ts-ignore
                keys: () => keys,
                // @ts-ignore
                entries: () => all,
                get: (n) => headers[n.toLowerCase()],
                has: (n) => n.toLowerCase() in headers,
            },
        })

        request.open(options.method || "get", url, true)

        request.onload = () => {
            request
                .getAllResponseHeaders()
                // @ts-ignore
                .replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, (m, key, value) => {
                    keys.push((key = key.toLowerCase()))
                    all.push([key, value])
                    headers[key] = headers[key]
                        ? `${headers[key]},${value}`
                        : value
                })
            resolve(response())
        }

        request.onerror = reject

        request.withCredentials = options.credentials == "include"

        for (const i in options.headers) {
            request.setRequestHeader(i, options.headers[i])
        }

        request.send(options.body || null)
    })
}

const _fetch =
    typeof fetch === "undefined"
        ? typeof window === "undefined"
            ? _f
            : window.fetch || _f
        : fetch

export const api = async <T>(
    endpoint: string,
    options?: {
        method?: string
        headers?: {
            [key: string]: string
        }
        body?: any
        signal?: AbortSignal
    },
    isApi2 = false
): Promise<T> => {
    if (typeof window === "undefined") {
        // ssr
        // @ts-ignore
        return context.ssrFetch(endpoint, options)
    }

    // try ssr cache
    const key = SSR.obj2str({ endpoint, options })
    // @ts-ignore
    if (window.__SSR_CACHE__) {
        // @ts-ignore
        const ssrVal = window.__SSR_CACHE__[key]
        console.log("SSR:", key, ssrVal)
        if (ssrVal) {
            return ssrVal
        }
    }

    let method = "GET"

    let headers: any = {
        "Content-Type": "application/json",
    }

    if (isApi2) {
        headers.Token = get(api2Key)
    } else {
        headers.Authorization = "Token token=" + get(apiKey)
    }

    if (options?.headers) headers = { ...headers, ...options.headers }
    if (options?.method) method = options.method.toUpperCase()

    let url = (isApi2 ? "/api/" : apiBaseURL) + endpoint

    const span = sentry.currentTransaction()?.startChild({
        op: "fetch",
        description: method + " " + url,
        data: Object.assign({}, options, { url }),
    })
    const trace_id = span?.toTraceparent()
    if (trace_id) {
        headers["sentry-trace"] = trace_id
    }

    const response = await _fetch(url, {
        method,
        mode: "cors",
        headers,
        body: options?.body ? JSON.stringify(options.body) : null,
        signal: options?.signal,
    })

    span?.finish()

    // @ts-ignore
    let data = (await response?.json()) || null

    if (response?.status < 200 || response?.status >= 400)
        throw { response, data }

    return data
}

let usersCache: User[]
let userMap: {
    [key: string]: User
} = {}

export const getUsers = async () => {
    if (usersCache) return usersCache
    usersCache = await api<User[]>("users")
    usersCache.forEach((u) => {
        userMap[u.id] = u
    })
    return usersCache
}

export const getUser = async (id: number) => {
    const users = await getUsers()
    for (let i = 0; i < users.length; i++) {
        if (users[i].id == id) {
            return users[i]
        }
    }
}

export const dateToDbString = (d: Date): string => {
    return (
        d.getFullYear() +
        "-" +
        (d.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        d.getDate().toString().padStart(2, "0")
    )
}

export const getPlan = async (
    user: User,
    page = 1,
    startDate?: Date,
    endDate?: Date,
    signal?: AbortSignal
) => {
    if (!startDate) {
        startDate = new Date()
        startDate.setDate(startDate.getDate() - 90)
    }
    if (!endDate) {
        endDate = new Date()
        endDate = new Date(
            endDate.getFullYear() + 1,
            endDate.getMonth(),
            endDate.getDate()
        )
    }

    const startDateStr = dateToDbString(startDate)
    const endDateStr = dateToDbString(endDate)

    return await api<PlanEntry[]>(
        "planning_entries?page=" +
            page +
            "&period=" +
            startDateStr +
            ":" +
            endDateStr +
            (user.id ? "&user_id=" + user.id : ""),
        {
            signal,
        }
    )
}

export const updatePlanEntry = async (id: number, data: any) => {
    return await api<PlanEntry>("planning_entries/" + id, {
        method: "put",
        body: data,
    })
}
export const createPlanEntry = async (data: any) => {
    return await api<PlanEntry>("planning_entries", {
        method: "post",
        body: data,
    })
}

export const deletePlanEntry = async (id: number) => {
    return await api<PlanEntry>("planning_entries/" + id, {
        method: "delete",
    })
}

export const getProjects = async (page = 1) => {
    return await api<Project[]>("projects?page=" + page)
}

export let projectCache: Project[] = []
const projectMap: {
    [key: string]: Project
} = {}

export const getProject = async (id: number) => {
    return projectMap[id]
}

let pPage = 1
;(async function () {
    while (true) {
        const r = await getProjects(pPage)
        projectCache = [...projectCache, ...r]
        r.forEach((p) => {
            projectMap[p.id] = p
        })
        if (r.length < 100) {
            break
        }
        pPage++
    }
})()

export const getTodo = async (user: User, signal?: AbortSignal) => {
    const r = await api<PlanEntry[]>(
        "todo-item?sort=sort" +
            (user?.id
                ? "&filter=" +
                  encodeURIComponent('{"user_id": ' + user.id + "}")
                : ""),
        { signal },
        true
    )

    r.forEach((t) => {
        t.user = userMap[t.user_id]
        if (projectMap[t.project_id]) {
            t.project = projectMap[t.project_id]
            t.project.customer_name = t.project.customer.name
        }
    })

    return r
}

export const updateTodo = async (id: string, data: any) => {
    return await api<PlanEntry>(
        "todo-item/" + id,
        {
            method: "put",
            body: data,
        },
        true
    )
}

export const createTodo = async (data: any) => {
    return await api<PlanEntry>(
        "todo-item",
        {
            method: "post",
            body: data,
        },
        true
    )
}

export const deleteTodo = async (id: string) => {
    return await api<PlanEntry>(
        "todo-item/" + id,
        {
            method: "delete",
        },
        true
    )
}

// export const getContent = async (path: string): Promise<Content> => {
//     const c = await api<Content[]>("content", { limit: 1, filter: { path } })
//     if (c?.data?.length) {
//         return c.data[0]
//     }
//     return null
// }
